import { useState, type FormEvent } from 'react';
import { useNavigate, Link } from 'react-router';
import { Lock, Mail, Eye, EyeOff } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { useThemeStore } from '../stores/useThemeStore';
import { useTranslation } from '../i18n';

const LoginPage = () => {
  const { login } = useAuth();
  const { isDark } = useThemeStore();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const err = await login(email, password);

    setLoading(false);

    if (err) {
      setError(err);
      return;
    }

    navigate('/');
  };

  return (
    <div
      className={`min-h-screen flex flex-col items-center justify-center px-4 ${
        isDark
          ? 'bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900'
          : 'bg-gradient-to-br from-vivatis-50 via-white to-vivatis-100'
      }`}
    >
      {/* Decorative background shapes */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div
          className={`absolute -top-24 -right-24 w-96 h-96 rounded-full blur-3xl ${
            isDark ? 'bg-vivatis-900/20' : 'bg-vivatis-200/40'
          }`}
        />
        <div
          className={`absolute -bottom-32 -left-32 w-[30rem] h-[30rem] rounded-full blur-3xl ${
            isDark ? 'bg-vivatis-900/15' : 'bg-vivatis-300/30'
          }`}
        />
      </div>

      {/* Login card */}
      <div
        className={`relative w-full max-w-md rounded-2xl shadow-xl border p-8 ${
          isDark
            ? 'bg-gray-800/80 backdrop-blur-sm border-gray-700'
            : 'bg-white/80 backdrop-blur-sm border-gray-200'
        }`}
      >
        {/* Logo area */}
        <div className="text-center mb-8">
          <img
            src="/VIV_Logo_main.svg"
            alt="Vivatis Pharma"
            className="h-12 w-auto mx-auto dark:brightness-0 dark:invert"
          />
          <p
            className={`text-sm font-medium mt-3 ${
              isDark ? 'text-gray-400' : 'text-gray-500'
            }`}
          >
            {t.brand.productDatabase}
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-5">
          {error && (
            <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-4 py-2 break-words overflow-hidden">
              {error}
            </div>
          )}

          {/* Email */}
          <div>
            <label
              htmlFor="email"
              className={`block text-sm font-medium mb-1.5 ${
                isDark ? 'text-gray-300' : 'text-gray-700'
              }`}
            >
              {t.login.email}
            </label>
            <div className="relative">
              <Mail
                size={16}
                className={`absolute left-3 top-1/2 -translate-y-1/2 ${
                  isDark ? 'text-gray-500' : 'text-gray-400'
                }`}
              />
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
                placeholder="your@email.com"
                className={`w-full pl-10 pr-4 py-2.5 text-sm rounded-lg border transition-colors focus:outline-none focus:ring-2 focus:ring-vivatis-500 focus:border-transparent ${
                  isDark
                    ? 'bg-gray-700 border-gray-600 text-gray-100 placeholder-gray-500'
                    : 'bg-gray-50 border-gray-300 text-gray-900 placeholder-gray-400'
                }`}
              />
            </div>
          </div>

          {/* Password */}
          <div>
            <label
              htmlFor="password"
              className={`block text-sm font-medium mb-1.5 ${
                isDark ? 'text-gray-300' : 'text-gray-700'
              }`}
            >
              {t.login.password}
            </label>
            <div className="relative">
              <Lock
                size={16}
                className={`absolute left-3 top-1/2 -translate-y-1/2 ${
                  isDark ? 'text-gray-500' : 'text-gray-400'
                }`}
              />
              <input
                id="password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="current-password"
                placeholder="••••••••"
                className={`w-full pl-10 pr-10 py-2.5 text-sm rounded-lg border transition-colors focus:outline-none focus:ring-2 focus:ring-vivatis-500 focus:border-transparent ${
                  isDark
                    ? 'bg-gray-700 border-gray-600 text-gray-100 placeholder-gray-500'
                    : 'bg-gray-50 border-gray-300 text-gray-900 placeholder-gray-400'
                }`}
              />
              <button
                type="button"
                onClick={() => setShowPassword((prev) => !prev)}
                className={`absolute right-3 top-1/2 -translate-y-1/2 transition-colors ${
                  isDark
                    ? 'text-gray-500 hover:text-gray-300'
                    : 'text-gray-400 hover:text-gray-600'
                }`}
                aria-label={showPassword ? t.login.hidePassword : t.login.showPassword}
              >
                {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>

          {/* Forgot password link */}
          <div className="text-right">
            <Link
              to="/forgot-password"
              className={`text-sm transition-colors ${
                isDark
                  ? 'text-vivatis-400 hover:text-vivatis-300'
                  : 'text-vivatis-600 hover:text-vivatis-700'
              }`}
            >
              {t.login.forgotPassword}
            </Link>
          </div>

          {/* Submit button */}
          <button
            type="submit"
            disabled={loading}
            className="w-full py-2.5 rounded-lg bg-vivatis-600 text-white font-medium text-sm hover:bg-vivatis-700 active:bg-vivatis-800 transition-colors cursor-pointer focus:outline-none focus:ring-2 focus:ring-vivatis-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? t.login.signingIn : t.login.signIn}
          </button>
        </form>
      </div>

      {/* Footer */}
      <p
        className={`relative mt-8 text-xs ${
          isDark ? 'text-gray-500' : 'text-gray-400'
        }`}
      >
        {t.brand.copyright}
      </p>
    </div>
  );
};

export default LoginPage;
